import { Injectable } from '@angular/core';

import { AnyCustomPropertyValue } from '@reach/interfaces/custom-property/custom-property-value.interface';
import { NodeCreationEdgeInterface } from '@reach/interfaces/edges/edges.types';
import {
	NodeCreationSource,
	NodeCreationSourceBody,
} from '@reach/interfaces/nodes/node-creation-source.interface';
import { NodeInterface } from '@reach/interfaces/nodes/nodes.types';
import { WebsiteUpdateBody } from '@reach/interfaces/websites/websites.requests';
import {
	AnalyzeWebsiteResponse,
	CreateWebsiteResponse,
	GetWebsiteFromUrlResponse,
	ReExtractWebsiteResponse,
	UpdateWebsiteResponse,
} from '@reach/interfaces/websites/websites.responses';

import { BaseApiService } from '~app-client/api/_internal';
import { ReachNodeId } from '~app-client/core/types';
import { WorkspaceId } from '~app-client/workspaces/types';
import { isPersistedWorkspaceId } from '~app-client/workspaces/utils';

@Injectable({ providedIn: 'root' })
export class WebsitesApiService extends BaseApiService {
	protected commonPath = ['websites'];

	public async checkExistence(url: string): Promise<GetWebsiteFromUrlResponse> {
		const query = { url };

		return await this.http.get<GetWebsiteFromUrlResponse>(this.url('by-url'), query);
	}

	public async create(
		url: string,
		workspaceIds: WorkspaceId[],
		source: NodeCreationSource,
		outgoingEdges: NodeCreationEdgeInterface[] = [],
		clientSideRendering?: boolean
	): Promise<CreateWebsiteResponse> {
		const sourceBody: NodeCreationSourceBody = { source };
		const persistedWorkspaceIds: string[] = workspaceIds.filter((id) =>
			isPersistedWorkspaceId(id)
		);
		return await this.http.post<CreateWebsiteResponse>(this.url(), {
			url,
			clientSideRendering,
			workspaceIds: persistedWorkspaceIds,
			outgoingEdges,
			...sourceBody,
		});
	}

	public async analyze(url: string, workspaceId: number): Promise<AnalyzeWebsiteResponse> {
		const query = {
			url,
			workspaceId: `${workspaceId}`,
		};

		return await this.http.post<AnalyzeWebsiteResponse>(this.url('analyze'), query);
	}

	public async updateCustomProperties(
		websiteId: ReachNodeId,
		customProperties: AnyCustomPropertyValue[],
		workspaceId: ReachNodeId
	): Promise<AnyCustomPropertyValue[]> {
		const body = {
			customProperties,
			workspaceId,
		};
		return await this.http
			.patch<NodeInterface>(this.url(websiteId), body)
			.then((node) => node?.customProperties || []);
	}

	public async edit(
		websiteId: ReachNodeId,
		data: WebsiteUpdateBody
	): Promise<UpdateWebsiteResponse> {
		const body = data;
		return await this.http.patch<UpdateWebsiteResponse>(this.url(websiteId), body);
	}

	public async reprocessWebsite(websiteId: ReachNodeId): Promise<void> {
		return await this.http.post(this.url('re-extract'), { nodeIds: [websiteId] });
	}

	public async reprocessWebsites(websiteIds: ReachNodeId[]): Promise<ReExtractWebsiteResponse> {
		return await this.http.post<ReExtractWebsiteResponse>(this.url('re-extract'), {
			nodeIds: websiteIds,
		});
	}
}
